import React, { useEffect, useState } from "react";
import { Trophy, Loader2, Plus, Play, X, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const ST = { SCHEDULED: "text-sky-300", OPEN: "text-sky-300", LOCKED: "text-amber-400", DRAWN: "text-emerald-400", CANCELLED: "text-slate-500" };
const blankTier = () => ({ label: "", prize_rupees: "", count: 1 });

export default function LuckyDraws() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: "", draw_date: "", description: "", tiers: [blankTier()] });

  const load = () => api.get("/admin/draws").then((r) => setD(r.data)).catch(() => toast.error("Failed to load draws"));
  useEffect(() => { load(); }, []);

  const setTier = (i, k, v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, blankTier()] }));
  const removeTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Draw name is required");
    const prize_tiers = form.tiers
      .filter((t) => t.prize_rupees && Number(t.count) > 0)
      .map((t) => ({ label: t.label || "", amount_paise: Math.round(Number(t.prize_rupees) * 100), count: Number(t.count) }));
    if (prize_tiers.length === 0) return toast.error("Add at least one prize tier with an amount and winner count");
    setBusy(true);
    try {
      await api.post("/admin/draws", { name: form.name, draw_date: form.draw_date || "", description: form.description, prize_tiers });
      toast.success("Draw created"); setOpen(false);
      setForm({ name: "", draw_date: "", description: "", tiers: [blankTier()] });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const execute = async (id) => {
    if (!window.confirm("Run this draw now? Winners are picked securely (one prize per customer) and prizes are paid from the Reward Fund. This locks the ticket pool and cannot be undone.")) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/draws/${id}/execute`); toast.success(`Draw complete — ${data.winners.length} winner(s), ${data.participants} entries`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this scheduled draw?")) return;
    try { await api.post(`/admin/draws/${id}/cancel`); toast.success("Draw cancelled"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-draws">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" /><h1 className="font-heading text-2xl font-bold">Lucky Draws</h1></div>
        <button data-testid="draw-create-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400"><Plus className="h-4 w-4" /> New draw</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="ledger-card rounded-2xl border-amber-500/30 p-5" data-testid="draws-reward-fund">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400/80">Reward / Lucky-draw fund (40%)</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-amber-400">{d.reward_fund_display}</p>
          <p className="mt-1 text-xs text-slate-500">Prizes are paid only from this fund — never allowed to go negative.</p>
        </div>
        <div className="ledger-card rounded-2xl p-5" data-testid="draws-eligible">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Eligible tickets (entries)</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-emerald-400">{d.eligible_count}</p>
          <p className="mt-1 text-xs text-slate-500">Tickets waiting to enter the next draw. Each ticket = one entry.</p>
        </div>
      </div>

      <div className="space-y-3">
        {d.draws.length === 0 && <div className="ledger-card rounded-2xl p-8 text-center text-slate-500">No draws yet. Create one to reward your ticket holders.</div>}
        {d.draws.map((x) => {
          const isDrawn = x.status === "DRAWN";
          const isOpen = expanded === x.drawId;
          return (
            <div key={x.drawId} data-testid={`draw-row-${x.drawId}`} className="ledger-card rounded-2xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading text-lg font-bold">{x.name}</p>
                  {x.description && <p className="text-xs text-slate-500">{x.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(x.prize_tiers_display || []).map((t, i) => (
                      <span key={i} className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2.5 py-1 text-xs text-amber-300">{t.label}: <b>{t.amount_display}</b> × {t.count}</span>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <span className={`font-mono text-xs ${ST[x.status] || ""}`}>{x.status}</span>
                  <p className="mt-0.5 text-xs text-slate-500">{isDrawn ? `${(x.winners || []).length} winner(s) · ${x.total_prize_display}` : `${x.total_winners} winner(s) · up to ${x.max_prize_display}`}</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                {(x.status === "SCHEDULED" || x.status === "OPEN" || x.status === "LOCKED") && (
                  <>
                    <button data-testid={`draw-execute-${x.drawId}`} onClick={() => execute(x.drawId)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50"><Play className="h-3 w-3" /> Run draw</button>
                    <button data-testid={`draw-cancel-${x.drawId}`} onClick={() => cancel(x.drawId)} className="rounded-full border border-rose-500/40 px-4 py-1.5 text-xs text-rose-300 hover:bg-rose-500/10">Cancel</button>
                  </>
                )}
                {isDrawn && (x.winners || []).length > 0 && (
                  <button data-testid={`draw-toggle-${x.drawId}`} onClick={() => setExpanded(isOpen ? null : x.drawId)} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-slate-500">
                    {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {isOpen ? "Hide" : "View"} winners
                  </button>
                )}
              </div>

              {isDrawn && isOpen && (
                <div className="mt-4 overflow-hidden rounded-xl border border-slate-800" data-testid={`draw-winners-${x.drawId}`}>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900/50 font-mono text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="p-3 text-left">Prize</th><th className="p-3 text-left">Ticket</th><th className="p-3 text-right">Amount</th></tr></thead>
                    <tbody>
                      {(x.winners || []).map((w, i) => (
                        <tr key={i} className="border-t border-slate-800/60"><td className="p-3 text-amber-300">{w.prize_label}</td><td className="p-3 font-mono text-xs text-emerald-400">{w.ticketCode}</td><td className="p-3 text-right font-mono">₹{(w.prize_amount_paise / 100).toLocaleString("en-IN")}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="border-t border-slate-800 bg-slate-900/40 p-3 font-mono text-[11px] text-slate-500">Pool: {x.eligible_ticket_count} entries · ref {x.locked_pool_reference} · {x.verification_status}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/80 p-5" data-testid="draw-modal">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-slate-700 bg-slate-900 p-6">
            <div className="flex items-center justify-between"><h2 className="font-heading text-lg font-bold">New Lucky Draw</h2><button onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="mt-4 space-y-3">
              <Input label="Draw name" testid="draw-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Draw date (optional)" testid="draw-date" type="date" value={form.draw_date} onChange={(v) => setForm({ ...form, draw_date: v })} />
              <Input label="Description (optional)" testid="draw-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />

              <div className="pt-2">
                <div className="flex items-center justify-between">
                  <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Prize tiers</label>
                  <button data-testid="draw-add-tier" onClick={addTier} className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"><Plus className="h-3 w-3" /> Add tier</button>
                </div>
                <div className="mt-2 space-y-2">
                  {form.tiers.map((t, i) => (
                    <div key={i} data-testid={`tier-row-${i}`} className="grid grid-cols-[1fr_90px_70px_auto] items-end gap-2 rounded-xl border border-slate-800 bg-slate-950/40 p-2">
                      <TierIn label="Label" testid={`tier-label-${i}`} placeholder={`Prize ${i + 1}`} value={t.label} onChange={(v) => setTier(i, "label", v)} />
                      <TierIn label="Amount ₹" testid={`tier-amount-${i}`} type="number" value={t.prize_rupees} onChange={(v) => setTier(i, "prize_rupees", v)} />
                      <TierIn label="Winners" testid={`tier-count-${i}`} type="number" value={t.count} onChange={(v) => setTier(i, "count", v)} />
                      <button data-testid={`tier-remove-${i}`} onClick={() => removeTier(i)} disabled={form.tiers.length === 1} className="mb-1 rounded-lg p-2 text-slate-500 hover:text-rose-400 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">e.g. 1st ₹5,000 × 1, 2nd ₹2,000 × 2, 3rd ₹1,000 × 5. One customer can win at most one prize per draw.</p>
              </div>
            </div>
            <button data-testid="draw-save-btn" onClick={create} disabled={busy} className="mt-5 w-full rounded-full bg-amber-500 py-2.5 font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50">{busy ? "Saving…" : "Create draw"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-widest text-slate-500">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-amber-500" />
    </div>
  );
}

function TierIn({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wide text-slate-600">{label}</label>
      <input data-testid={testid} type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-700 bg-slate-950/60 px-2 py-1.5 text-sm outline-none focus:border-amber-500" />
    </div>
  );
}
